import VariantPicker from 'variant-picker';
import { Component } from 'component';
import { debounce } from 'utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from 'theme-events';
import { ADD_TO_CART_TEXT_ANIMATION_DURATION } from 'product-form';
import { morph } from 'morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 *
 * @extends {Component<Refs>}
 */
class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  #isQuickAddOpen = false;

  #selectedOptionUrl = '';

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#selectedOptionUrl = link.href;

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener('quick-add-open', this.#handleQuickAddOpen);
    this.addEventListener('quick-add-close', this.#handleQuickAddClose);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);

    if (this.dataset.productVariantsSize === '1') return;

    link.addEventListener('click', this.navigateToProduct);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.productCardLink.removeEventListener('click', this.navigateToProduct);
  }

  /**
   * Handles the quick add open event.
   * Removes the href attribute from the product card link and sets isQuickAddOpen to true.
   */
  #handleQuickAddOpen = () => {
    this.refs.productCardLink.removeAttribute('href');
    this.#isQuickAddOpen = true;
  };

  /**
   * Handles the quick add close event.
   * Resets the href attribute on the product card link and sets isQuickAddOpen to false.
   */
  #handleQuickAddClose = () => {
    this.#isQuickAddOpen = false;

    // TODO: Why is there a timeout here?
    setTimeout(() => {
      this.refs.productCardLink.href = this.#selectedOptionUrl ?? '';
    });
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;
  };

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      this.#selectedOptionUrl = anchorElement.href;

      if (this.#isQuickAddOpen) return;

      this.refs.productCardLink.href = anchorElement.href;
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('variant-picker');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   */
  previewImage() {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
    }
  }

  /**
   * Resets the image to the variant image.
   */
  resetImage() {
    const { slideshow } = this.refs;
    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const defaultSlide = slideshow.defaultSlide;
    const slideId = defaultSlide?.getAttribute('slide-id');
    if (defaultSlide && slideshow.slides?.includes(defaultSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    } else if (!this.variantPicker?.selectedOption) {
      slideshow.previous(undefined, { animate: false });
      return;
    }

    const id = this.variantPicker.selectedOption.dataset.optionMediaId;
    if (!id) {
      slideshow.previous(undefined, { animate: false });
      return;
    }

    slideshow.select({ id }, undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof HTMLAnchorElement)) return;

    const productCardAnchor = event.target.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = event.target.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }
    history.pushState({}, '', url);
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

class QuickAdd extends Component {
  /** @type {HTMLElement|null} */
  #quickAddButton = null;
  /** @type {HTMLElement|null} */
  #quickAddBlocks = null;
  /** @type {HTMLElement|null} */
  #variantPicker = null;
  /** @type {HTMLElement|null} */
  #addToCart = null;
  /** @type {MutationObserver|null} */
  #mutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.queryChildrenAndAddAllEventListeners();

    this.#mutationObserver = new MutationObserver((mutations) => {
      // Observe mutations where the form gets re-mounted
      if (mutations.some((m) => m.type === 'childList')) {
        // Clean up any event listeners that might exist on existing or removed
        // elements to avoid double-registering
        this.removeAllEventListeners();

        this.queryChildrenAndAddAllEventListeners();
      }
    });

    this.#mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeAllEventListeners();
    this.#mutationObserver?.disconnect();
  }

  queryChildrenAndAddAllEventListeners() {
    this.#quickAddButton = this.querySelector('[data-quick-add-button]');
    this.#quickAddBlocks = this.querySelector('.quick-add__blocks');
    this.#variantPicker = this.querySelector('variant-picker');
    this.#addToCart = this.querySelector('.add-to-cart__button');

    this.#quickAddButton?.addEventListener('click', this.handleClick);
    this.#addToCart?.addEventListener('click', this.handleAddToCart);

    const parentItem = this.closest('product-card');
    if (!(parentItem instanceof HTMLElement)) return;

    const variantPicker = this.#quickAddBlocks?.querySelector('variant-picker');
    if (variantPicker instanceof HTMLElement) {
      const fieldsets = variantPicker.querySelectorAll('fieldset');
      if (fieldsets.length === 1 && parentItem.querySelector('swatches-variant-picker-component')) {
        this.#quickAddBlocks = null;
      }
    }

    this.#variantPicker?.addEventListener('scroll', this.#handleScroll);
  }

  removeAllEventListeners() {
    this.#quickAddButton?.removeEventListener('click', this.handleClick);
    this.#addToCart?.removeEventListener('click', this.handleAddToCart);
    this.#variantPicker?.removeEventListener('scroll', this.#handleScroll);
  }

  /**
   * Handles the add to cart event.
   */
  handleAddToCart = () => {
    setTimeout(() => {
      this.closeQuickAdd();
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  };

  /**
   * Closes the quick add.
   */
  closeQuickAdd() {
    if (!this.#quickAddBlocks || !this.#quickAddButton) return;

    // The delay is added to let this quickly animate out.
    this.#quickAddBlocks.classList.add('hiding');
    setTimeout(() => {
      if (!this.#quickAddBlocks || !this.#quickAddButton) return;
      this.#quickAddBlocks.classList.add('hidden');
      this.#quickAddBlocks.classList.remove('hiding');
      this.#quickAddButton.classList.remove('hidden');
    }, 125);

    this.dispatchEvent(new CustomEvent('quick-add-close', { bubbles: true }));
    document.removeEventListener('click', this.handleClickOutside);
  }

  /**
   * Unchecks the variant.
   * @param {HTMLInputElement} input - The input element.
   */
  uncheckVariant(input) {
    input.removeAttribute('checked');
    input.checked = false;
  }

  /**
   * Handles the click event.
   * @param {PointerEvent} event - The click event.
   */
  handleClick = (event) => {
    if (!this.#quickAddBlocks || (event.target instanceof Node && this.#quickAddBlocks?.contains(event.target))) return;

    if (!this.#quickAddButton) return;

    this.#quickAddButton.classList.add('hidden');
    this.#quickAddBlocks.classList.remove('hidden');
    this.dispatchEvent(new CustomEvent('quick-add-open', { bubbles: true }));

    if (this.#addToCart) this.#updateCartShadow();

    setTimeout(() => {
      document.addEventListener('click', this.handleClickOutside);
    }, 0);
  };

  /**
   * Handles the click outside event.
   * @param {PointerEvent} event - The click outside event.
   */
  handleClickOutside = (event) => {
    if (this.#shouldIgnoreClick(event)) return;

    this.handleQuickAddClose(event);
  };

  /**
   * Determines if the click should be ignored.
   * @param {PointerEvent} event - The pointer event.
   * @returns {boolean} Whether the click should be ignored.
   */
  #shouldIgnoreClick({ target }) {
    if (!(target instanceof HTMLElement)) return false;

    const parentElement = this.closest('product-card');
    if (!parentElement) return false;

    const productSwatches = parentElement.querySelector('swatches-variant-picker-component');
    const isAddToCartVisible = this.#quickAddBlocks?.querySelector('.add-to-cart__button.hidden') === null;
    const isClickOnQuickAddButton = this.#quickAddButton?.contains(target);
    const isClickOnVariant = target.tagName === 'INPUT';
    const isClickOnUnavailableVariant = target.dataset.optionAvailable === 'false';
    const isClickWithinQuickAddBlocks = this.#quickAddBlocks?.contains(target);
    const isClickWithinProductSwatches = productSwatches instanceof HTMLElement && productSwatches.contains(target);

    return Boolean(
      isClickOnQuickAddButton ||
        isClickWithinProductSwatches ||
        (isClickWithinQuickAddBlocks && isAddToCartVisible && isClickOnVariant) ||
        (isClickWithinQuickAddBlocks && (!isClickOnVariant || isClickOnUnavailableVariant))
    );
  }

  /**
   * Handles the quick add close event.
   * @param {Event} event - The event.
   */
  handleQuickAddClose(event) {
    const target = /** @type {Node|null} */ (event.target);
    if (!target) return;

    const closingDelay = this.#quickAddBlocks?.contains(target) ? 400 : 0;
    setTimeout(() => this.closeQuickAdd(), closingDelay);
  }

  /**
   * Handles the scroll event.
   */
  #handleScroll = () => {
    this.#updateCartShadow();
  };

  /**
   * Adds a shadow to the add to cart button when scrolling.
   */
  #updateCartShadow() {
    if (!(this.#variantPicker instanceof HTMLElement) || !(this.#addToCart instanceof HTMLElement)) return;

    const isAtBottom =
      Math.abs(this.#variantPicker.scrollHeight - this.#variantPicker.scrollTop - this.#variantPicker.clientHeight) < 1;

    if (isAtBottom) {
      this.#addToCart.classList.remove('add-shadow');
    } else {
      this.#addToCart.classList.add('add-shadow');
    }
  }
}

if (!customElements.get('quick-add')) {
  customElements.define('quick-add', QuickAdd);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
